import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {
  TDisplayedGeometries,
  TGeometryIds,
} from 'App/features/map2DEntities/prescriptionsEditor/prescriptionTypes';
import {RootState} from 'App/store/rootReducer';

type PrescriptionsEditorSlice = {
  displayedGeometries: TDisplayedGeometries;
  currentEditionId: string | null;
  isEditionFormEdited: boolean;
  isGeometryEdited: boolean;
};

const initialState: PrescriptionsEditorSlice = {
  displayedGeometries: {},
  currentEditionId: null,
  isEditionFormEdited: false,
  isGeometryEdited: false,
};

const prescriptionsEditorSlice = createSlice({
  name: 'prescriptionsEditor',
  initialState,
  reducers: {
    displayGeometries: (
      state,
      {payload}: PayloadAction<TDisplayedGeometries>,
    ) => {
      state.displayedGeometries = payload;
    },
    toggleGeometriesDisplayed: (
      state,
      {payload}: PayloadAction<TGeometryIds>,
    ) => {
      for (const id of payload) {
        if (!state.displayedGeometries[id]) {
          state.displayedGeometries[id] = {isHighlighted: false};
        } else {
          delete state.displayedGeometries[id];
        }
      }
    },
    toggleGeometriesHighlighted: (
      state,
      {payload}: PayloadAction<TGeometryIds>,
    ) => {
      for (const id of payload) {
        if (state.displayedGeometries[id]) {
          state.displayedGeometries[id] = {
            isHighlighted: !state.displayedGeometries[id].isHighlighted,
          };
        } else {
          state.displayedGeometries[id] = {isHighlighted: true};
        }
      }
    },
    toggleCategoryDisplayed: (
      state,
      {payload}: PayloadAction<TGeometryIds>,
    ) => {
      const atLeastOneDisplayed = Object.keys(state.displayedGeometries).some(
        id => payload.includes(id),
      );

      if (atLeastOneDisplayed) {
        for (const id of payload) {
          if (state.displayedGeometries[id]) {
            delete state.displayedGeometries[id];
          }
        }
      } else {
        for (const id of payload) {
          state.displayedGeometries[id] = {isHighlighted: false};
        }
      }
    },
    setCurrentEditionId: (state, {payload}: PayloadAction<string | null>) => {
      state.currentEditionId = payload;
    },
    setPrescriptionFormEdited: (state, {payload}: PayloadAction<boolean>) => {
      state.isEditionFormEdited = payload;
    },
    setGeometryEdited: (state, {payload}: PayloadAction<boolean>) => {
      state.isGeometryEdited = payload;
    },
    resetPrescriptionsEditor: () => initialState,
  },
});

export const selectDisplayedGeometries = (
  state: RootState,
): TDisplayedGeometries =>
  state.map2DEntities.prescriptionsEditor.displayedGeometries;
export const selectCurrentEditionId = (state: RootState): string | null =>
  state.map2DEntities.prescriptionsEditor.currentEditionId;
export const selectIsPrescriptionEdited = (state: RootState): boolean =>
  state.map2DEntities.prescriptionsEditor.isEditionFormEdited ||
  state.map2DEntities.prescriptionsEditor.isGeometryEdited;

export const {
  displayGeometries,
  toggleGeometriesDisplayed,
  toggleGeometriesHighlighted,
  toggleCategoryDisplayed,
  setCurrentEditionId,
  setPrescriptionFormEdited,
  setGeometryEdited,
  resetPrescriptionsEditor,
} = prescriptionsEditorSlice.actions;

export default prescriptionsEditorSlice.reducer;
